import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/api';
import { LunchOrder, ApiError } from '../types';
import {
  Calendar, UtensilsCrossed, Loader2, ChevronDown, ChevronUp,
  Building2, User, Download,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useAuthStore } from '../store/authStore';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export const History: React.FC = () => {
  const { user } = useAuthStore();
  const { t, i18n } = useTranslation();
  const [orders, setOrders] = useState<LunchOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedDate, setExpandedDate] = useState<string | null>(null);

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      setLoading(true); setError('');
      const allOrders = await apiClient.getMyOrders();
      setOrders(
        allOrders
          .filter(o => o.status === 'active')
          .sort((a, b) => new Date(b.lunchDate).getTime() - new Date(a.lunchDate).getTime()),
      );
    } catch (err) {
      setError((err as ApiError).message || t('history.errorLoad'));
    } finally { setLoading(false); }
  };

  const exportToExcel = () => {
    const locale = i18n.language === 'en' ? 'en-US' : 'es-AR';
    const exportData = orders.map(o => ({
      [t('common.date')]:      formatDate(o.lunchDate),
      [t('common.user')]:      o.userName,
      [t('common.email')]:     o.userEmail,
      [t('common.restaurant')]: o.restaurantName,
      [t('common.dish')]:      o.menuItemName,
      [t('common.price')]:     o.menuItemPrice ? `$${o.menuItemPrice.toFixed(2)}` : 'N/A',
      [t('common.status')]:    o.status === 'active' ? t('common.active') : t('common.cancelled'),
      [t('common.orderDate')]: new Date(o.createdAt).toLocaleString(locale),
    }));
    const total = orders.reduce((s, o) => s + (o.menuItemPrice || 0), 0);
    exportData.push({
      [t('common.date')]: '', [t('common.user')]: '', [t('common.email')]: '',
      [t('common.restaurant')]: '', [t('common.dish')]: t('common.total'),
      [t('common.price')]: `$${total.toFixed(2)}`, [t('common.status')]: '',
      [t('common.orderDate')]: '',
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('history.title'));
    ws['!cols'] = [{ wch: 12 }, { wch: 20 }, { wch: 30 }, { wch: 25 }, { wch: 35 }, { wch: 12 }, { wch: 12 }, { wch: 20 }];
    XLSX.writeFile(wb, `historial-pedidos-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const formatDate = (dateStr: string) => {
    const locale = i18n.language === 'en' ? 'en-US' : 'es-AR';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString(locale, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const ordersByDate = orders.reduce<Record<string, LunchOrder[]>>((acc, o) => {
    (acc[o.lunchDate] ??= []).push(o);
    return acc;
  }, {});
  const dates = Object.keys(ordersByDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-1">{t('history.title')}</h1>
          <p className="text-muted-foreground">{t('history.subtitle')}</p>
        </div>
        {orders.length > 0 && (
          <Button onClick={exportToExcel} variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            {t('common.exportExcel')}
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      {orders.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <UtensilsCrossed className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">{t('history.empty')}</h3>
            <p className="text-muted-foreground">{t('history.emptyDesc')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {dates.map((date) => {
            const dateOrders = ordersByDate[date];
            const isExpanded = expandedDate === date;
            const isToday   = date === new Date().toISOString().split('T')[0];

            return (
              <div
                key={date}
                className={`bg-card rounded-lg shadow-sm border overflow-hidden ${
                  isToday ? 'ring-2 ring-primary' : 'border-border'
                }`}
              >
                <button
                  onClick={() => setExpandedDate(isExpanded ? null : date)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors duration-150"
                >
                  <div className="flex items-center gap-4">
                    <Calendar className="w-5 h-5 text-primary flex-shrink-0" />
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground capitalize">{formatDate(date)}</p>
                        {isToday && <Badge variant="default">{t('history.today')}</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t(dateOrders.length === 1 ? 'history.order_one' : 'history.order_other', { count: dateOrders.length })}
                      </p>
                    </div>
                  </div>
                  {isExpanded
                    ? <ChevronUp   className="w-5 h-5 text-muted-foreground" />
                    : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                </button>

                {isExpanded && (
                  <div className="border-t border-border bg-muted/30">
                    {dateOrders.map((order) => (
                      <div key={order.orderId} className="px-6 py-4 border-b border-border last:border-b-0">
                        <div className={`grid grid-cols-1 md:grid-cols-2 ${
                          user?.role === 'ADMIN' ? 'lg:grid-cols-5' : 'lg:grid-cols-4'
                        } gap-4`}>
                          {user?.role === 'ADMIN' && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">{t('common.user')}</p>
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-muted-foreground" />
                                <div>
                                  <p className="font-medium text-foreground">{order.userName}</p>
                                  <p className="text-xs text-muted-foreground">{order.userEmail}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">{t('common.restaurant')}</p>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-muted-foreground" />
                              <p className="font-medium text-foreground">{order.restaurantName}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">{t('common.dish')}</p>
                            <div className="flex items-center gap-2">
                              <UtensilsCrossed className="w-4 h-4 text-muted-foreground" />
                              <p className="font-medium text-foreground">{order.menuItemName}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">{t('common.price')}</p>
                            <p className="font-medium text-success">
                              {order.menuItemPrice ? `$${order.menuItemPrice.toFixed(2)}` : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">{t('common.orderDate')}</p>
                            <p className="text-sm text-foreground">
                              {new Date(order.createdAt).toLocaleString(
                                i18n.language === 'en' ? 'en-US' : 'es-AR',
                                { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {user?.role === 'ADMIN' && (
                      <div className="px-6 py-4 bg-primary/5 border-t-2 border-primary/20">
                        <div className="flex justify-between items-center">
                          <p className="font-bold text-foreground">{t('history.totalDay')}</p>
                          <p className="font-bold text-success text-lg">
                            ${dateOrders.reduce((s, o) => s + (o.menuItemPrice || 0), 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
